"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildActivityScriptHelpers = buildActivityScriptHelpers;
const VariableStore_1 = require("../engine/VariableStore");
const ScriptEvaluator_1 = require("../engine/ScriptEvaluator");
const characterScriptContext_1 = require("../engine/characterScriptContext");
const characterActivityState_1 = require("./characterActivityState");
const evaluateActivityPlayable_1 = require("./evaluateActivityPlayable");
const computeActivityListRows_1 = require("./computeActivityListRows");
const incompleteActivityChecks_1 = require("./incompleteActivityChecks");
const activitySpinnerMarkup_1 = require("./activitySpinnerMarkup");
const activitySceneHelpers_1 = require("./activitySceneHelpers");
function buildCharacterScriptAccess(variableStore, player, gameSession, adventureManager, evaluateConditionScript) {
    const getVariableForCharacter = (characterName, variableName) => {
        if (!variableStore) {
            return undefined;
        }
        return (0, characterScriptContext_1.readVariableForCharacter)(variableStore, gameSession, player, characterName, variableName);
    };
    const evaluateConditionForCharacter = (0, characterScriptContext_1.createEvaluateConditionForCharacter)(gameSession, player, (script, targetPlayer) => {
        if (targetPlayer.id === player.id && evaluateConditionScript) {
            return evaluateConditionScript(script);
        }
        if (!variableStore || !adventureManager) {
            return false;
        }
        try {
            return ScriptEvaluator_1.ScriptEvaluator.executeCondition(script, targetPlayer, gameSession, variableStore, adventureManager);
        }
        catch {
            return false;
        }
    });
    return { getVariableForCharacter, evaluateConditionForCharacter };
}
/**
 * Read-only script helpers for activity state. Pass `evaluateConditionScript` from {@link ScriptEvaluator}
 * so `activityAvailable` can evaluate adventure `Requires:` scripts; omit in interpolation-only contexts.
 */
function buildActivityScriptHelpers(variableStore, player, gameSession, adventureManager, evaluateConditionScript, characterScriptAccess) {
    const charAccess = characterScriptAccess ??
        buildCharacterScriptAccess(variableStore, player, gameSession, adventureManager, evaluateConditionScript);
    const getAdventureActivities = () => {
        const adv = adventureManager?.getAdventure(gameSession.adventureId);
        return adv?.activities ?? [];
    };
    const getDef = (id) => {
        return getAdventureActivities().find((a) => a.id === id);
    };
    const getValidSceneIds = () => {
        const adv = adventureManager?.getAdventure(gameSession.adventureId);
        return new Set((adv?.scenes ?? []).map((s) => s.id).filter(Boolean));
    };
    const buildSceneChainContext = (typesCsv, parentActivityId, sort) => ({
        activities: getAdventureActivities(),
        characterName: player.characterName,
        validSceneIds: getValidSceneIds(),
        filter: {
            typesCsv,
            ...(parentActivityId !== undefined ? { parentActivityId } : {}),
            sort: sort === 'name' ? 'name' : 'list_order'
        }
    });
    const getVar = (name) => {
        if (!variableStore) {
            return undefined;
        }
        const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
        return variableStore.getVariable(name, ctx);
    };
    /** Matches server activity-list hydration when {@code evaluateConditionScript} is omitted. */
    const evaluateRequiresScript = (script) => charAccess.evaluateConditionForCharacter(player.characterName, script);
    const activityStateForCharacter = (characterName) => ({
        getVariable: (name) => charAccess.getVariableForCharacter(characterName, name),
        evaluateRequiresScript: (script) => charAccess.evaluateConditionForCharacter(characterName, script)
    });
    const hasIncompleteForCharacter = (characterName, activityIdsCsv) => {
        const { getVariable, evaluateRequiresScript: evalRequires } = activityStateForCharacter(characterName);
        return (0, incompleteActivityChecks_1.hasIncompleteActivitiesForCharacter)(activityIdsCsv, characterName, getDef, getVariable, evalRequires);
    };
    const incompleteMarkupForCharacter = (characterName, activityIdsCsv) => {
        const { getVariable, evaluateRequiresScript: evalRequires } = activityStateForCharacter(characterName);
        return (0, incompleteActivityChecks_1.generateIncompleteActivityListForCharacter)(activityIdsCsv, characterName, getDef, getVariable, evalRequires);
    };
    const readStateForCharacter = (characterName) => (0, characterActivityState_1.readActivitySetsFromVariableValues)((name) => charAccess.getVariableForCharacter(characterName, name));
    return {
        activityCompleted: (activityId) => {
            const state = (0, characterActivityState_1.readActivitySetsFromVariableValues)(getVar);
            return state.completed.has(String(activityId));
        },
        activityCompletedForCharacter: (characterName, activityId) => {
            const state = readStateForCharacter(characterName);
            return state.completed.has(String(activityId));
        },
        activityInProgress: (activityId) => {
            const state = (0, characterActivityState_1.readActivitySetsFromVariableValues)(getVar);
            return state.inProgress.has(String(activityId));
        },
        activityInProgressForCharacter: (characterName, activityId) => {
            const state = readStateForCharacter(characterName);
            return state.inProgress.has(String(activityId));
        },
        activityVisibleForCharacter: (activityId, characterName) => {
            const def = getDef(String(activityId));
            if (!def) {
                return false;
            }
            const char = (characterName ?? player.characterName).trim() || player.characterName;
            return (0, evaluateActivityPlayable_1.evaluateActivityVisibleForCharacter)(def, char);
        },
        /**
         * True when prerequisite activities, active parents, and {@code Requires:} script (if any)
         * are satisfied for the current character. Does not require the activity to be incomplete.
         */
        activityRequirementsMet: (activityId) => {
            const def = getDef(String(activityId));
            if (!def) {
                return false;
            }
            const state = (0, characterActivityState_1.readActivitySetsFromVariableValues)(getVar);
            return (0, evaluateActivityPlayable_1.evaluateActivityRequirementsMet)(def, player.characterName, state, getAdventureActivities(), evaluateRequiresScript);
        },
        activityRequirementsMetForCharacter: (characterName, activityId) => {
            const def = getDef(String(activityId));
            if (!def) {
                return false;
            }
            const state = readStateForCharacter(characterName);
            return (0, evaluateActivityPlayable_1.evaluateActivityRequirementsMet)(def, characterName, state, getAdventureActivities(), (script) => charAccess.evaluateConditionForCharacter(characterName, script));
        },
        activityAvailable: (activityId) => {
            const def = getDef(String(activityId));
            if (!def) {
                return false;
            }
            const state = (0, characterActivityState_1.readActivitySetsFromVariableValues)(getVar);
            return (0, evaluateActivityPlayable_1.evaluateActivityPlayable)(def, player.characterName, state, getAdventureActivities(), evaluateRequiresScript);
        },
        /**
         * Display {@link ActivityDefinition.name} for an activity id in the current adventure.
         * Returns an empty string when the id is missing, unknown, or not loaded in {@code adventureManager}.
         * Safe for rich-text '''script''' fragments and {@code ${}}-style contexts that evaluate expressions.
         */
        activityName: (activityId) => {
            const id = String(activityId ?? '').trim();
            if (!id) {
                return '';
            }
            const def = getDef(id);
            return def?.name ? String(def.name) : '';
        },
        /**
         * Configured {@link ActivityDefinition.startSceneId} for an activity in the current adventure.
         * Returns an empty string when the id is unknown.
         */
        getActivityStartScene: (activityId) => {
            const id = String(activityId ?? '').trim();
            if (!id) {
                return '';
            }
            const def = getDef(id);
            const sid = def?.startSceneId ? String(def.startSceneId).trim() : '';
            return sid;
        },
        /**
         * Scene id for an activity's {@link ActivityDefinition.scenePrefix} + postfix (e.g. {@code points}).
         * Returns {@code ""} when the prefix, postfix, or resolved scene is missing from the adventure.
         */
        getActivityScene: (activityId, scenePostfix) => {
            const id = String(activityId ?? '').trim();
            if (!id) {
                return '';
            }
            return (0, activitySceneHelpers_1.getActivitySceneForCharacter)(buildSceneChainContext(), id, String(scenePostfix ?? '').trim());
        },
        /**
         * First eligible activity's postfix scene in list order for the current character.
         * {@code typesCsv} and {@code parentActivityId} use the same rules as {@link collectActivityIds}.
         */
        getFirstActivityScene: (typesCsv, parentActivityId, scenePostfix) => (0, activitySceneHelpers_1.getFirstActivitySceneInGroup)(buildSceneChainContext(typesCsv, parentActivityId), String(scenePostfix ?? '').trim()),
        /**
         * Next eligible activity's postfix scene after {@code currentActivityId}, or {@code terminalSceneId}
         * when the current id is last in the ordered eligible list.
         */
        getNextActivityScene: (currentActivityId, typesCsv, parentActivityId, scenePostfix, terminalSceneId) => (0, activitySceneHelpers_1.getNextActivitySceneInGroup)(buildSceneChainContext(typesCsv, parentActivityId), String(currentActivityId ?? '').trim(), String(scenePostfix ?? '').trim(), String(terminalSceneId ?? '').trim()),
        /**
         * Resolved postfix scene id without checking adventure scene existence (for display/conditions).
         */
        resolveActivitySceneId: (activityId, scenePostfix) => {
            const id = String(activityId ?? '').trim();
            if (!id) {
                return '';
            }
            return (0, activitySceneHelpers_1.resolveActivitySceneId)(getDef(id), String(scenePostfix ?? '').trim());
        },
        /**
         * Row count for an {@code <Activity-List>} with the same filters as the embed (and server row hydration).
         * {@code typesCsv} is comma-separated type ids (e.g. {@code "task"}). Omit or empty types → {@code 0}.
         * When {@code hideLocked} is true, locked rows are excluded the same as {@code hide_locked=true} on the embed.
         * Optional {@code parentActivityId}: list children of that parent; omit for top-level activities only.
         * Activity {@code Requires:} scripts are evaluated with {@link ScriptEvaluator} when no custom evaluator is passed.
         */
        activityListRowCount: (typesCsv, hideLocked, parentActivityId) => {
            const adv = adventureManager?.getAdventure(gameSession.adventureId);
            if (!adv?.activities?.length) {
                return 0;
            }
            const types = (0, incompleteActivityChecks_1.parseActivityIdsCsv)(typesCsv);
            if (types.length === 0) {
                return 0;
            }
            const parentRaw = (parentActivityId ?? '').toString().trim();
            const state = (0, characterActivityState_1.readActivitySetsFromVariableValues)(getVar);
            const resumeScenes = (0, characterActivityState_1.readActivityResumeScenesFromVariableValues)(getVar);
            return (0, computeActivityListRows_1.computeActivityListRows)({ activities: adv.activities, scenes: adv.scenes }, player.characterName, state, {
                sort: 'list_order',
                types,
                ...(parentRaw ? { parentActivityId: parentRaw } : {}),
                ...(hideLocked ? { hideLocked: true } : {})
            }, evaluateRequiresScript, resumeScenes).length;
        },
        /**
         * Comma-separated activity ids from the current adventure, filtered by optional type, parent, and exclude lists.
         * Use with {@link hasIncompleteActivitiesFromList} when adventures prefer dynamic id lists over hard-coded CSV in scenes.
         */
        collectActivityIds: (typesCsv, excludeIdsCsv, parentActivityId, sort) => (0, incompleteActivityChecks_1.collectActivityIdsCsv)(getAdventureActivities(), {
            typesCsv,
            excludeIdsCsv,
            ...(parentActivityId !== undefined ? { parentActivityId } : {}),
            ...(sort !== undefined ? { sort: sort === 'name' ? 'name' : 'list_order' } : {})
        }),
        /**
         * Comma-separated spinner values from filtered activities (activity {@link ActivityDefinition.name} by default).
         * Same filters as {@link collectActivityIds}. Optional {@code sort} is {@code list_order} (default) or {@code name}.
         */
        collectActivitySpinnerValues: (typesCsv, parentActivityId, sort) => (0, activitySpinnerMarkup_1.collectActivitySpinnerValuesCsv)(getAdventureActivities(), {
            typesCsv,
            ...(parentActivityId !== undefined ? { parentActivityId } : {}),
            sort: sort === 'name' ? 'name' : 'list_order'
        }),
        /**
         * Returns a {@code <Spinner>} tag whose {@code values} are built from adventure activities (not hard-coded).
         * Use in scene descriptions inside '''script''' blocks, e.g.
         * {@code getActivitySpinnerMarkup("my_var", "mission", "list_order")}.
         */
        getActivitySpinnerMarkup: (variable, typesCsv, sort, parentActivityId) => {
            const values = (0, activitySpinnerMarkup_1.collectActivitySpinnerValuesCsv)(getAdventureActivities(), {
                typesCsv: (typesCsv ?? 'mission').trim() || 'mission',
                ...(parentActivityId !== undefined ? { parentActivityId } : {}),
                sort: sort === 'name' ? 'name' : 'list_order'
            });
            return (0, activitySpinnerMarkup_1.buildActivitySpinnerMarkupTag)(variable, values);
        },
        /**
         * True when every eligible activity in {@code activityIdsCsv} is completed for the current character.
         */
        areActivitiesCompleteInList: (activityIdsCsv) => !hasIncompleteForCharacter(player.characterName, activityIdsCsv),
        /**
         * Same as {@link areActivitiesCompleteInList} for an arbitrary character name.
         */
        areActivitiesCompleteInListForCharacter: (characterName, activityIdsCsv) => !hasIncompleteForCharacter(characterName, activityIdsCsv),
        /**
         * True when any activity id in {@code activityIdsCsv} is eligible for the current character
         * (including {@code Requires:} when present) and not yet completed.
         */
        hasIncompleteActivitiesFromList: (activityIdsCsv) => hasIncompleteForCharacter(player.characterName, activityIdsCsv),
        /**
         * Same as {@link hasIncompleteActivitiesFromList} for an arbitrary character name.
         */
        hasIncompleteActivitiesFromListForCharacter: hasIncompleteForCharacter,
        /**
         * Same as {@link hasIncompleteActivitiesFromList} for the other player in the session.
         */
        hasPartnerIncompleteActivitiesFromList: (activityIdsCsv) => {
            const partnerChar = (0, characterScriptContext_1.getPartnerCharacterName)(gameSession, player);
            if (!partnerChar) {
                return false;
            }
            return hasIncompleteForCharacter(partnerChar, activityIdsCsv);
        },
        /**
         * Bullet list of incomplete activity names from {@code activityIdsCsv} for the current character.
         * Returns an empty string when every eligible activity in the list is completed.
         */
        generateIncompleteActivityListMarkup: (activityIdsCsv) => incompleteMarkupForCharacter(player.characterName, activityIdsCsv),
        /**
         * Same as {@link generateIncompleteActivityListMarkup} for an arbitrary character name.
         */
        generateIncompleteActivityListMarkupForCharacter: incompleteMarkupForCharacter
    };
}
//# sourceMappingURL=activityScriptHelpers.js.map